import type { MemberSearchParams } from '../types/member.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub の react-query キーファクトリ。認証(現在ユーザー)・週次レポート・acm_records・revenue__daily・売上の版つき台帳・画面マップ・リリース履歴。
 * @graph-connects none
 */
export const queryKeys = {
  auth: {
    current: ['auth', 'current'] as const,
  },
  weeklyReports: {
    all: ['weekly-reports'] as const,
    list: () => ['weekly-reports', 'list'] as const,
    detail: (id: string) => ['weekly-reports', 'detail', id] as const,
  },
  kpi: {
    records: (from?: string, to?: string) => ['kpi', 'records', from ?? null, to ?? null] as const,
  },
  breakthrough: {
    records: (from?: string, to?: string) =>
      ['breakthrough', 'records', from ?? null, to ?? null] as const,
  },
  revenue: {
    records: (from?: string, to?: string) =>
      ['revenue', 'records', from ?? null, to ?? null] as const,
    validationLogs: ['revenue', 'validation-logs'] as const,
    ledgerMonthly: (version: string, fromMonth: string, toMonth: string) =>
      ['revenue', 'ledger', 'monthly', version, fromMonth, toMonth] as const,
    ledgerDaily: (from: string, to: string) => ['revenue', 'ledger', 'daily', from, to] as const,
    ledgerCompare: (
      leftVersion: string,
      rightVersion: string,
      fromMonth: string,
      toMonth: string,
    ) => ['revenue', 'ledger', 'compare', leftVersion, rightVersion, fromMonth, toMonth] as const,
    ledgerChecks: (version: string) => ['revenue', 'ledger', 'checks', version] as const,
    ledgerDefinitions: ['revenue', 'ledger', 'definitions'] as const,
    ledgerAll: ['revenue', 'ledger'] as const,
    ledgerClosings: (version: string, fromMonth: string, toMonth: string) =>
      ['revenue', 'ledger', 'closings', version, fromMonth, toMonth] as const,
  },
  codeMap: {
    screens: ['code-map', 'screens'] as const,
  },
  releaseHistory: {
    timeline: (category?: string, uxImpact?: string) =>
      ['release-history', 'timeline', category ?? null, uxImpact ?? null] as const,
  },
  withdrawalAnalysis: {
    data: ['withdrawal-analysis', 'data'] as const,
  },
  csInquiry: {
    dashboard: (from?: string, to?: string) =>
      ['cs-inquiry', 'dashboard', from ?? null, to ?? null] as const,
    chats: (from?: string, to?: string, state?: string, limit?: number, offset?: number) =>
      [
        'cs-inquiry',
        'chats',
        from ?? null,
        to ?? null,
        state ?? null,
        limit ?? 50,
        offset ?? 0,
      ] as const,
    chatDetail: (userChatId: string) => ['cs-inquiry', 'chat-detail', userChatId] as const,
    importStatus: ['cs-inquiry', 'import-status'] as const,
    aiAnalysisLatest: ['cs-inquiry', 'ai-analysis', 'latest'] as const,
    aiAnalysisHistory: ['cs-inquiry', 'ai-analysis', 'history'] as const,
    aiAnalysisDetail: (id: string) => ['cs-inquiry', 'ai-analysis', 'detail', id] as const,
  },
  dataCatalog: {
    overview: ['data-catalog', 'overview'] as const,
    columns: (datasetId: string, tableId: string) =>
      ['data-catalog', 'columns', datasetId, tableId] as const,
  },
  notifications: {
    channels: ['notifications', 'channels'] as const,
    rules: ['notifications', 'rules'] as const,
  },
  stylistPerformance: {
    summary: (month?: string | null, stylistId?: string | null) =>
      ['stylist-performance', 'summary', month ?? null, stylistId ?? null] as const,
    evaluationTrend: (months?: number | null, stylistId?: string | null) =>
      ['stylist-performance', 'evaluation-trend', months ?? 6, stylistId ?? null] as const,
    orders: (params: Record<string, unknown>) => ['stylist-performance', 'orders', params] as const,
    orderDetail: (orderId: number | null) =>
      ['stylist-performance', 'order-detail', orderId] as const,
  },
  stylingAnalysis: {
    dailyStylistStats: (from?: string, to?: string) =>
      ['styling-analysis', 'daily-stylist-stats', from ?? null, to ?? null] as const,
    requestTrends: (from?: string, to?: string) =>
      ['styling-analysis', 'request-trends', from ?? null, to ?? null] as const,
    requests: (from?: string, to?: string, limit?: number, offset?: number) =>
      ['styling-analysis', 'requests', from ?? null, to ?? null, limit ?? 50, offset ?? 0] as const,
    dailyDetails: (date?: string | null, type?: string | null, stylistId?: string) =>
      ['styling-analysis', 'daily-details', date ?? null, type ?? null, stylistId ?? null] as const,
    aiTrendsLatest: ['styling-analysis', 'ai-trends', 'latest'] as const,
    aiTrendsHistory: ['styling-analysis', 'ai-trends', 'history'] as const,
    aiTrendsDetail: (id: string) => ['styling-analysis', 'ai-trends', 'detail', id] as const,
    dailyPerformance: (from?: string, to?: string) =>
      ['styling-analysis', 'daily-performance', from ?? null, to ?? null] as const,
    ratingAnalysis: (from?: string, to?: string, dateBase?: string) =>
      ['styling-analysis', 'rating-analysis', from ?? null, to ?? null, dateBase ?? null] as const,
    requestInsights: (from?: string, to?: string) =>
      ['styling-analysis', 'request-insights', from ?? null, to ?? null] as const,
  },
  funnelAnalysis: {
    registration: (from?: string, to?: string) =>
      ['funnel-analysis', 'registration', from ?? null, to ?? null] as const,
  },
  dataIntegrity: {
    summary: ['data-integrity', 'summary'] as const,
    exclusions: ['data-integrity', 'exclusions'] as const,
  },
  faqMonitor: {
    worklist: (runDate?: string) => ['faq-monitor', 'worklist', runDate ?? null] as const,
    alfPerformance: (query: { from?: string; to?: string; minCited: number }) =>
      [
        'faq-monitor',
        'alf-performance',
        query.from ?? null,
        query.to ?? null,
        query.minCited,
      ] as const,
  },
  costAccounting: {
    calculate: (month: string) => ['cost-accounting', 'calculate', month] as const,
    syncStatus: ['cost-accounting', 'sync-status'] as const,
  },
  member: {
    // 絞り込み軸が増えても取り違えないよう、位置引数ではなく検索条件そのものを受け取る
    list: (params: MemberSearchParams) =>
      [
        'member',
        'list',
        params.environment ?? 'production',
        params.page ?? 1,
        params.limit ?? 50,
        params.arthurUserId ?? null,
        params.lastName ?? null,
        params.firstName ?? null,
        params.email ?? null,
        params.tel ?? null,
        params.subscriptionStatus ?? null,
        params.rentalStatuses === undefined ? null : [...params.rentalStatuses].sort().join(','),
        params.planName ?? null,
        params.ticketHolding ?? null,
        params.sortBy ?? null,
        params.sortOrder ?? null,
      ] as const,
    profile: (id: number) => ['member', 'profile', id] as const,
    payments: (id: number) => ['member', 'payments', id] as const,
    orders: (id: number) => ['member', 'orders', id] as const,
    tickets: (id: number) => ['member', 'tickets', id] as const,
    coupons: (id: number) => ['member', 'coupons', id] as const,
    addresses: (id: number) => ['member', 'addresses', id] as const,
  },
  transcription: {
    all: ['transcription'] as const,
    list: () => ['transcription', 'list'] as const,
    detail: (id: string) => ['transcription', 'detail', id] as const,
  },
  budgetActual: {
    targets: (from?: string, to?: string, granularity?: string) =>
      ['budget-actual', 'targets', from ?? null, to ?? null, granularity ?? null] as const,
    offerBreakdown: (from?: string, to?: string) =>
      ['budget-actual', 'offer-breakdown', from ?? null, to ?? null] as const,
  },
  couponUsage: {
    summary: ['coupon-usage', 'summary'] as const,
    users: (couponMasterId: string) => ['coupon-usage', 'users', couponMasterId] as const,
  },
  permissions: {
    me: ['permissions', 'me'] as const,
    roles: ['permissions', 'roles'] as const,
    userRoles: ['permissions', 'user-roles'] as const,
  },
  memberDemographics: {
    all: ['member-demographics'] as const,
  },
  purchaseAnalysis: {
    daily: (from?: string, to?: string) =>
      ['purchase-analysis', 'daily', from ?? null, to ?? null] as const,
  },
  keyIssues: {
    list: ['key-issues', 'list'] as const,
  },
  reports: {
    all: ['reports'] as const,
    list: (category?: string) => ['reports', 'list', category ?? null] as const,
    detail: (id: string) => ['reports', 'detail', id] as const,
  },
  itemCatalog: {
    all: ['item-catalog'] as const,
  },
  stylingReview: {
    list: (filter: string) => ['stylingReview', 'list', filter] as const,
    detail: (phoenixOrderId: number) => ['stylingReview', 'detail', phoenixOrderId] as const,
  },
  knowledgeMeta: {
    search: (keyword: string, section?: string) =>
      ['knowledgeMeta', 'search', keyword, section ?? null] as const,
    detail: (docId: string) => ['knowledgeMeta', 'detail', docId] as const,
  },
  issueMgmt: {
    list: (query: string, themeId: string | null, includeHidden: boolean, unlinkedLimit: number) =>
      ['issue-mgmt', 'list', query, themeId, includeHidden, unlinkedLimit] as const,
    lists: ['issue-mgmt', 'list'] as const,
    detail: (issueId: string) => ['issue-mgmt', 'detail', issueId] as const,
    details: ['issue-mgmt', 'detail'] as const,
    history: (issueId: string) => ['issue-mgmt', 'history', issueId] as const,
    histories: ['issue-mgmt', 'history'] as const,
    domains: ['issue-mgmt', 'domains'] as const,
    members: ['issue-mgmt', 'members'] as const,
    themes: ['issue-mgmt', 'themes'] as const,
    settings: ['issue-mgmt', 'settings'] as const,
    cases: (issueId: string) => ['issue-mgmt', 'cases', issueId] as const,
    casesAll: ['issue-mgmt', 'cases'] as const,
    backlogSync: ['issue-mgmt', 'backlog-sync'] as const,
    caseCandidates: ['issue-mgmt', 'case-candidates'] as const,
    staleNotify: ['issue-mgmt', 'stale-notify'] as const,
    todoCards: ['issue-mgmt', 'todo', 'cards'] as const,
    excluded: (limit: number) => ['issue-mgmt', 'todo', 'excluded', limit] as const,
    excludedAll: ['issue-mgmt', 'todo', 'excluded'] as const,
    ingestStatus: ['issue-mgmt', 'ingest', 'status'] as const,
    inspection: ['issue-mgmt', 'ingest', 'inspection'] as const,
  },
  productBacklog: {
    list: ['product-backlog', 'list'] as const,
    draft: ['product-backlog', 'draft'] as const,
    moves: ['product-backlog', 'moves'] as const,
  },
  coorde: {
    capacity: ['coorde', 'capacity'] as const,
    runs: ['coorde', 'runs'] as const,
    run: (runId: string) => ['coorde', 'run', runId] as const,
  },
} as const;
